import React, { useState, useMemo, useEffect } from "react";
import ImageCarousel from "../../components/ImageCarousel/ImageCarousel";
import { useNavigate } from "react-router-dom";
import AnalysisResultV2 from "../../components/AnalysisResult/AnalysisResultV2";
import ArtisanList from "../../components/ArtisanList/ArtisanList";
import { Spinner, Button, Card, Row, Col, Form, Accordion, Modal } from "react-bootstrap";
import ExportOptionsModal from "../../components/ExportModal/ExportOptionsModal";
import axios from "axios";
import { useRenovCredits } from "../../context/RenovCreditsContext";
import { useAuth } from "../../context/AuthContext";

const RoomViewer = ({
  room,
  images,
  comment,
  setComment,
  analysisResult,
  analysisType,
  onEdit,
  onAnalyze,
  artisans,
  loadingArtisans,
  artisansRef,
  handleShowArtisans,
  userPlan,
  hasEnoughCredits,
  analyses,
  selectedAnalysis,
  onSelectAnalysis,
  isNewAnalysisMode,
  onDeleteAnalysis,
  onStartNewAnalysis,
  imageModifications,
  loadingImageModifications,
  embedded = false,
}) => {
  const navigate = useNavigate();
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [isGeneratingDiy, setIsGeneratingDiy] = useState(false);
  const { user } = useAuth();
  const { renovCredits, setRenovCredits } = useRenovCredits();
  const diyCost = userPlan === "Pro" ? 10 : 20;
  const canGenerateDiy = (renovCredits || 0) >= diyCost;
  const [isArtisanSectionVisible, setIsArtisanSectionVisible] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportOptions, setExportOptions] = useState({ template: 'summary', includePhotos: false, includeComments: true, orientation: 'portrait', format: 'A4' });
  const [shouldGenerateVisuals, setShouldGenerateVisuals] = useState(true);
  const [modificationViewState, setModificationViewState] = useState({}); // { key: 'original' | 'modified' }
  const [lightbox, setLightbox] = useState({ visible: false, src: '', title: '', prompt: '' });
  const [showVisualsConfirmation, setShowVisualsConfirmation] = useState(false);

  const visualsCost = userPlan === "Pro" ? (images?.length || 0) * 1 : (images?.length || 0) * 2;

  const handleAnalysisClick = () => {
    if (shouldGenerateVisuals && images && images.length > 0) {
      setShowVisualsConfirmation(true);
    } else {
      onAnalyze("corps", { shouldGenerateVisuals: false });
    }
  };

  const handleConfirmWithVisuals = () => {
    onAnalyze("corps", { shouldGenerateVisuals: true });
    setShowVisualsConfirmation(false);
  };

  const handleConfirmNoVisuals = () => {
    onAnalyze("corps", { shouldGenerateVisuals: false });
    setShouldGenerateVisuals(false);
    setShowVisualsConfirmation(false);
  };

  const filteredModifications = useMemo(() => {
    if (!Array.isArray(imageModifications)) {
      return [];
    }
    if (!selectedAnalysis || isNewAnalysisMode) {
      return imageModifications;
    }
    return imageModifications.filter((mod) => mod && String(mod.analysis_id) === String(selectedAnalysis.id));
  }, [imageModifications, selectedAnalysis, isNewAnalysisMode]);

  const hasFilteredModifications = filteredModifications.length > 0;
  const emptyModificationsMessage = !selectedAnalysis || isNewAnalysisMode ? 'Aucune ambiance generee pour cette piece.' : 'Aucune ambiance generee pour cette analyse.';

  const toggleModificationVisual = (key) => {
    setModificationViewState((prev) => ({
      ...prev,
      [key]: (prev[key] || 'modified') === 'original' ? 'modified' : 'original',
    }));
  };

  const openLightbox = (src, title, prompt) => {
    if (!src) return;
    setLightbox({ visible: true, src, title: title || '', prompt: prompt || '' });
  };

  const closeLightbox = () => {
    setLightbox({ visible: false, src: '', title: '', prompt: '' });
  };

  useEffect(() => {
    setModificationViewState({});
    setLightbox({ visible: false, src: '', title: '', prompt: '' });
  }, [selectedAnalysis?.id, filteredModifications.length]);

  const handleDownloadPdf = async () => {
    const analysisToExport = isNewAnalysisMode
      ? analysisResult
      : selectedAnalysis?.analysis_result;
    const commentToExport = isNewAnalysisMode
      ? comment
      : selectedAnalysis?.user_comment;

    if (!analysisToExport) return;

    const payload = {
      ...analysisToExport,
      commentaire_user: commentToExport,
    };

    setIsDownloadingPdf(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/pdf/generate-analysis`,
        {
          analysisData: payload,
        },
        {
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "analyse-renovestim.pdf");
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erreur lors du téléchargement du PDF :", error);
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const handleOpenExport = () => setShowExportModal(true);
  const handleCloseExport = () => setShowExportModal(false);
  const handleConfirmExport = async (opts) => {
    setExportOptions(opts || exportOptions);
    setShowExportModal(false);
    const analysisToExport = isNewAnalysisMode ? analysisResult : selectedAnalysis?.analysis_result;
    const commentToExport = isNewAnalysisMode ? comment : selectedAnalysis?.user_comment;
    if (!analysisToExport) return;
    try {
      setExporting(true);
      let res;
      const isLocal = typeof window !== 'undefined' && (
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1' ||
        window.location.port === '3000'
      );
      const forceServerless = String(process.env.REACT_APP_FORCE_SERVERLESS || '').toLowerCase() === '1' || String(process.env.REACT_APP_FORCE_SERVERLESS || '').toLowerCase() === 'true';
      console.debug('[PDF] env flags', { isLocal, forceServerless, opts });

      if (isLocal && !forceServerless) {
        // In CRA dev, serverless route doesn't exist; call backend directly to avoid 404 noise
        const payload = { ...analysisToExport, commentaire_user: commentToExport };
        console.debug('[PDF] Local dev → using backend /pdf/generate-analysis');
        res = await axios.post(
          `${process.env.REACT_APP_SERVER_URL}/pdf/generate-analysis`,
          { analysisData: payload },
          { responseType: 'blob' }
        );
      } else {
        // In prod (Vercel), use serverless function
        console.debug('[PDF] Prod → calling /api/export-pdf', opts);
        try {
          const toDataUrl = async (url) => {
            try {
              const r = await fetch(url, { cache: 'no-store' });
              const b = await r.blob();
              return await new Promise((resolve, reject) => {
                const fr = new FileReader();
                fr.onloadend = () => resolve(fr.result);
                fr.onerror = reject;
                fr.readAsDataURL(b);
              });
            } catch (e) {
              console.warn('[PDF] photo fetch failed (client)', url, e?.message);
              return null;
            }
          };
          const rawUrls = (images || [])
            .map((img) => (typeof img === 'string' ? img : (img?.url || img?.src)))
            .filter(Boolean)
            .map((u) => {
              if (/^https?:\/\//i.test(u)) return u;
              const base = (process.env.REACT_APP_SERVER_URL || '').replace(/\/$/, '');
              if (u.startsWith('/')) { return `${base}${u}`; }
              return `${base}/${u}`;
            });
          const limit = (opts?.includePhotos ? (opts?.template === 'full' ? 12 : 8) : 0);
          const photoData = limit ? (await Promise.all(rawUrls.slice(0, limit).map(toDataUrl))).filter(Boolean) : [];
          res = await axios.post(
            `/api/export-pdf`,
            {
              analysis: analysisToExport,
              comment: commentToExport,
              context: {
                superficie_piece: room?.superficie,
                localisation_projet: room?.property_localisation,
                age_logement_annees: room?.age_logement_annees,
                piece_label: (room?.label || room?.name || room?.type || room?.room_name || ''),
                property_label: (room?.properties?.title || room?.properties?.name || room?.property_title || room?.property_titre || room?.property_name || room?.property_label || room?.property_address || ''),
              },
              options: opts,
              photos: photoData,
            },
            { responseType: 'blob' }
          );
          console.debug('[PDF] serverless OK', { status: res?.status, size: res?.data?.size ?? 'blob' });
        } catch (err) {
          console.warn('[PDF] Serverless failed, using backend as fallback', err?.response?.status, err?.message, err?.response?.data);
        const payload = { ...analysisToExport, commentaire_user: commentToExport };
        console.debug('[PDF] Local dev → using backend /pdf/generate-analysis');
        res = await axios.post(
            `${process.env.REACT_APP_SERVER_URL}/pdf/generate-analysis`,
            { analysisData: payload },
            { responseType: 'blob' }
          );
        }
      }
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'rapport-renovestim.pdf');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Erreur export PDF', e);
    } finally {
      setExporting(false);
    }
  };

  const handleShowArtisansClick = () => {
    setIsArtisanSectionVisible(true);
    handleShowArtisans();
  };

  const handleGenerateDiy = async () => {
    try {
      setIsGeneratingDiy(true);
      const analysisToUse = isNewAnalysisMode ? analysisResult : selectedAnalysis?.analysis_result;
      const commentToUse = isNewAnalysisMode ? comment : selectedAnalysis?.user_comment;
      if (!analysisToUse) return;

      if (!user?.id) { console.error('User non connecté'); return; }
      // Endpoint tout-en-un: consomme RC, génère et télécharge
      const pdfRes = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/openai/generate-diy-playbook-pdf`,
        {
          userId: user.id,
          analysis: { ...analysisToUse, commentaire_user: commentToUse },
          contexte: {
            superficie_piece: room?.superficie,
            localisation_projet: room?.property_localisation,
            age_logement_annees: room?.age_logement_annees,
          },
        },
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(pdfRes.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'tutoriel-renovestim.pdf');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      // décrémenter instantanément les RC côté UI
      setRenovCredits(Math.max(0, (renovCredits || 0) - diyCost));
    } catch (e) {
      if (e?.response?.status === 402) {
        alert("Crédits insuffisants. Rendez-vous dans Mon Compte pour recharger vos RC.");
      } else {
        console.error('Erreur génération tutoriel DIY:', e);
      }
    } finally {
      setIsGeneratingDiy(false);
    }
  };

  return (
    <>
      {!embedded && (
        <>
          <h2 style={{ color: "#ff8c00" }}>{room.name}</h2>
          <ImageCarousel images={images} />
          <p>
            <strong>Superficie :</strong> {room.superficie} m²
          </p>
        </>
      )}

      <Card className="analysis-panel mb-4">
        <Card.Header className="analysis-panel-header">
          <div className="d-flex justify-content-between align-items-center">
            <div className="analysis-heading"><i className="fas fa-note-sticky me-2"></i>Contexte & historique</div>
            <div><i className="fas fa-clipboard-list me-2"></i>Résultats de l’analyse</div>
            <div><i className="fas fa-clipboard-list me-2"></i>Résultats de l’analyse</div>
            <div><i className="fas fa-microscope me-2"></i>Analyse de la pièce</div>
            <div className="text-muted small">RC disponibles: {renovCredits ?? 0}</div>
          </div>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={8} className="mb-3 mb-md-0">
              <label htmlFor="comment" className="form-label fw-semibold">Commentaire</label>
              <textarea
                className="form-control analysis-comment"
                id="comment"
                rows="4"
                placeholder="Ajoutez un contexte (optionnel) pour une analyse plus précise"
                value={isNewAnalysisMode ? comment : selectedAnalysis?.user_comment || ""}
                onChange={(e) => setComment(e.target.value)}
                disabled={!isNewAnalysisMode}
              ></textarea>
            </Col>
            <Col md={4}>
              <div className="analysis-history">
                <div className="history-header fw-semibold mb-2">Historique</div>
                {analyses && analyses.length > 0 ? (
                  <ul className="history-list">
                    {analyses.map((analysis) => {
                      const isSelected = selectedAnalysis?.id === analysis.id;
                      const handleSelect = () => onSelectAnalysis(analysis);
                      const handleKeyDown = (e) => {
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSelect(); }
                      };
                      return (
                        <li
                          key={analysis.id}
                          className={`history-item ${isSelected ? "selected" : ""}`}
                          onClick={handleSelect}
                          role="button"
                          tabIndex={0}
                          onKeyDown={handleKeyDown}
                        >
                          <div className="history-item-main">
                            <span className="date">
                              {new Date(analysis.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="history-item-actions">
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); onDeleteAnalysis(analysis.id); }}
                              title="Supprimer l'analyse"
                            >
                              <i className="fas fa-trash"></i>
                            </Button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <div className="text-muted small">Aucune analyse enregistrée pour l’instant.</div>
                )}
              </div>
            </Col>
          </Row>
        </Card.Body>
        <Card.Footer className="analysis-actions d-flex justify-content-between align-items-center">
          <Form.Check 
            type="switch"
            id="visuals-switch"
            label="Generer les ambiances visuelles"
            checked={shouldGenerateVisuals}
            onChange={(e) => setShouldGenerateVisuals(e.target.checked)}
            disabled={!isNewAnalysisMode}
          />
          {!hasEnoughCredits(analysisType) ? (
            <button className="analyze-button-dark" onClick={() => navigate("/moncompte")}>
              Recharger mes RC
            </button>
          ) : (
            <>
              {isNewAnalysisMode ? (
                <button onClick={handleAnalysisClick} className="analyze-button-dark">
                  <i className="fas fa-play me-2"></i>
                  Analyser la pièce ({userPlan === "Free" ? 10 : 5} RC)
                </button>
              ) : (
                <button onClick={onStartNewAnalysis} className="analyze-button-dark">
                  <i className="fas fa-plus me-2"></i>
                  Nouvelle Analyse
                </button>
              )}
            </>
          )}
        </Card.Footer>
      </Card>

      {(isNewAnalysisMode ? analysisResult : selectedAnalysis?.analysis_result) && (
        <div className="mt-4">
          <Card className="result-panel">
            <Card.Header className="result-panel-header d-flex justify-content-between align-items-center">
              <h4>Résultat de l'analyse :</h4>
              <Button className="result-export-btn" variant="outline-secondary" onClick={handleOpenExport} disabled={exporting}>
                {exporting ? <Spinner as="span" animation="border" size="sm" /> : <i className="bi bi-file-earmark-pdf"></i>}
                {" "}Exporter…
              </Button>
            </Card.Header>
            <Card.Body>
              <AnalysisResultV2 result={isNewAnalysisMode ? analysisResult : selectedAnalysis.analysis_result} />
            </Card.Body>
          </Card>

          <ExportOptionsModal
            show={showExportModal}
            onClose={handleCloseExport}
            onConfirm={handleConfirmExport}
            initialOptions={exportOptions}
          />


          {(loadingImageModifications || hasFilteredModifications) && (
            <Accordion className="mt-4">
              <Accordion.Item eventKey="google-visuals">
                <Accordion.Header>
                  <i className="bi bi-stars me-2"></i>
                                    Visuels générés
                </Accordion.Header>
                <Accordion.Body>
                  {loadingImageModifications ? (
                    <div className="text-center">
                      <Spinner animation="border" variant="primary" />
                      <p className="mt-2">Recuperation des images generees...</p>
                    </div>
                  ) : !hasFilteredModifications ? (
                    <p className="text-muted mb-0">{emptyModificationsMessage}</p>
                  ) : (
                    <Row xs={1} md={2} className="g-4">
                      {filteredModifications.map((modification, index) => {
                        if (!modification || !modification.modified_image_url) {
                          return null;
                        }
                        const stateKey = String(`${modification.analysis_id ?? 'room'}-${modification.id ?? index}`);
                        const mode = modificationViewState[stateKey] || 'modified';
                        const hasOriginal = Boolean(modification.base_image_url);
                        const showOriginal = mode === 'original' && hasOriginal;
                        const imageSrc = showOriginal ? modification.base_image_url : modification.modified_image_url;
                        const createdAtLabel = modification.created_at ? new Date(modification.created_at).toLocaleString() : 'Date inconnue';
                        return (
                          <Col key={stateKey}>
                            <Card>
                              <Card.Img
                                variant="top"
                                src={imageSrc}
                                alt={showOriginal ? "Image originale" : "Image generee"}
                                style={{ height: "320px", objectFit: "cover", cursor: "zoom-in" }}
                                role="button"
                                onClick={() =>
                                  openLightbox(
                                    imageSrc,
                                    showOriginal ? "Image originale" : "Image generee",
                                    modification.prompt
                                  )
                                }
                              />
                              <Card.Body>
                                <Card.Text className="small text-muted mb-2">{createdAtLabel}</Card.Text>
                                {modification.prompt && (
                                  <Card.Text className="fst-italic mb-3">"{modification.prompt}"</Card.Text>
                                )}
                                {hasOriginal && (
                                  <Button variant="outline-secondary" size="sm" onClick={() => toggleModificationVisual(stateKey)}>
                                    {showOriginal ? "Voir l'image generee" : "Voir l'image originale"}
                                  </Button>
                                )}
                              </Card.Body>
                            </Card>
                          </Col>
                        );
                      })}
                    </Row>
                  )}
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          )}

          <div className="d-flex justify-content-between mt-4" style={{ gap: "10px" }}>
            <button className="room-action-button green" onClick={handleGenerateDiy} disabled={isGeneratingDiy || !canGenerateDiy}>
              <strong>Je fais moi-meme</strong>
              <br />
              {isGeneratingDiy ? 'Generation du tutoriel...' : `Generer le tutoriel (PDF) - ${diyCost} RC`}
            </button>
            <button className="room-action-button orange" onClick={handleShowArtisansClick}>
              <strong>Je fais faire</strong>
              <br />
              Afficher la liste des artisans
            </button>
          </div>

          {isArtisanSectionVisible && (
            <div ref={artisansRef}>
              {loadingArtisans ? (
                <div className="text-center mt-4">
                  <Spinner animation="border" variant="primary" />
                  <p>Recherche des artisans...</p>
                </div>
              ) : (
                <ArtisanList artisans={artisans} />
              )}
            </div>
          )}
        </div>
      )}
      <Modal show={lightbox.visible} onHide={closeLightbox} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>{lightbox.title || 'Aperçu de l\'image'}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {lightbox.src && (
            <img
              src={lightbox.src}
              alt={lightbox.title || 'Image generee'}
              style={{ maxWidth: '100%', maxHeight: '80vh', width: 'auto', height: 'auto', objectFit: 'contain' }}
            />
          )}
          {lightbox.prompt && (
            <p className="mt-3 text-muted fst-italic">"{lightbox.prompt}"</p>
          )}
        </Modal.Body>
      </Modal>

      <Modal show={showVisualsConfirmation} onHide={() => setShowVisualsConfirmation(false)} centered>
        <Modal.Header closeButton>
            <Modal.Title>Confirmer la génération d'ambiances</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <p>
                Vous allez générer {images?.length || 0} ambiance(s) visuelle(s) pour cette pièce.
            </p>
            <p>
                Le coût est de <strong>{visualsCost} RC</strong> ({userPlan === 'Pro' ? '1 RC' : '2 RC'} par image).
            </p>
            <p>Continuer ?</p>
        </Modal.Body>
        <Modal.Footer>
            <Button variant="secondary" onClick={handleConfirmNoVisuals}>
                Analyser sans générer d'images
            </Button>
            <Button variant="primary" onClick={handleConfirmWithVisuals}>
                Confirmer et générer
            </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default RoomViewer;
